import { AllowOrder, StopOrder, UnitOrder, UnitOrderImportResult } from 'types';
import { fetchPaginated } from './fetch-paginated';
import fetchWithRetry from './fetch-retry';

export const fetchUnitOrders = fetchPaginated('unit-orders');

export const saveUnitOrders = async (unitOrders: UnitOrder[]): Promise<UnitOrder[] | null> => {
  const url: URL = new URL('/api/unit-orders', window.location.origin);
  const init: RequestInit = {
    method: 'POST',
    headers: new Headers({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(unitOrders)
  };
  return await fetchWithRetry<UnitOrder[] | null>(url, init, null);
};

export const getAllowOrders = async (unitNumber: string): Promise<AllowOrder[]> => {
  const url: URL = new URL(`/api/unit-orders/${unitNumber}?allowed=true`, window.location.origin);
  return await fetchWithRetry<AllowOrder[]>(url, { method: 'GET' }, []);
};

export const getStopOrders = async (unitNumber: string): Promise<StopOrder[]> => {
  const url: URL = new URL(`/api/unit-orders/${unitNumber}?allowed=false`, window.location.origin);
  return await fetchWithRetry<StopOrder[]>(url, { method: 'GET' }, []);
};

/*
 * Uploads a CSV to the bulk import endpoint. A validation failure returns a structured 4xx body
 * with a message, so the body is parsed regardless of status and its message is surfaced.
 */
const postUnitOrderImport = async (file: File, commit: boolean): Promise<UnitOrderImportResult> => {
  const token = localStorage.getItem('token');
  const url = new URL('/api/unit-orders/import', window.location.origin);
  url.searchParams.append('commit', String(commit));
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(url.toString(), {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData
  });
  const body = (await response.json().catch(() => null)) as (UnitOrderImportResult & { message?: string }) | null;
  if (!response.ok || !body || !body.rows) {
    throw new Error(body?.message ?? `Import failed (HTTP ${response.status})`);
  }
  return body;
};

export const previewUnitOrderImport = (file: File): Promise<UnitOrderImportResult> => postUnitOrderImport(file, false);

export const commitUnitOrderImport = (file: File): Promise<UnitOrderImportResult> => postUnitOrderImport(file, true);

export const downloadUnitOrderImportTemplate = (): void => {
  const token = localStorage.getItem('token');
  fetch('/api/unit-orders/import/template', { headers: { Authorization: `Bearer ${token}` } })
    .then(res => res.blob())
    .then(blob => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'unit-orders-import-template.csv';
      a.click();
      URL.revokeObjectURL(url);
    });
};

export type InboundOrderCheck = { allowed: boolean; message: string | null };

/** A failed lookup is unavailable, never an empty list that could be mistaken for permission. */
export const checkInboundOrder = async (
  unitNumber: string,
  customerId: number,
  signal?: AbortSignal
): Promise<InboundOrderCheck> => {
  const url = new URL(
    `/api/unit-orders/${encodeURIComponent(unitNumber.trim().toUpperCase())}/inbound-check`,
    window.location.origin
  );
  url.searchParams.set('customerId', String(customerId));
  const result = await fetchWithRetry<InboundOrderCheck | null>(
    url,
    { method: 'GET', signal, cache: 'no-store' },
    null
  );
  if (
    !result ||
    typeof result.allowed !== 'boolean' ||
    (result.message !== null && typeof result.message !== 'string') ||
    (!result.allowed && !result.message?.trim())
  ) {
    throw new Error('Unable to verify stop / allow orders');
  }
  return result;
};
